import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Clock, Truck, RotateCcw } from "lucide-react";

const WHATSAPP_NUMBER = "17865064537";

export default function Contact() {
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hi KK Beauty & Fashion! I have a question."
  )}`;

  const methods = [
    {
      icon: Phone,
      title: "Call Us",
      lines: ["1-800-484-7959"],
      note: "Toll-free customer care",
      href: "tel:18004847959",
      cta: "Call now",
      testid: "contact-phone",
    },
    {
      icon: Mail,
      title: "Email Us",
      lines: ["welcome@kkbeautyfashion.com"],
      note: "We reply within 1 business day",
      href: "mailto:welcome@kkbeautyfashion.com",
      cta: "Send email",
      testid: "contact-email",
    },
    {
      icon: MessageCircle,
      title: "WhatsApp Chat",
      lines: ["+1 786-506-4537"],
      note: "Fastest way to reach us",
      href: waHref,
      cta: "Start chat",
      testid: "contact-whatsapp",
      external: true,
    },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 md:px-8">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
        <p className="label-eyebrow">Guest Services</p>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight md:text-5xl" data-testid="contact-title">
          Contact Us
        </h1>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-500">
          We're here to help with orders, products, shipping, and returns. Reach our
          KK Beauty &amp; Fashion care team using any of the options below.
        </p>
      </motion.div>

      {/* Contact methods */}
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {methods.map((m) => (
          <div key={m.title} className="flex flex-col border border-zinc-200 bg-white p-7" data-testid={m.testid}>
            <m.icon size={24} className="text-kkaccent" />
            <h2 className="font-display mt-4 text-lg">{m.title}</h2>
            {m.lines.map((l) => (
              <p key={l} className="mt-1 text-sm font-medium text-zinc-900">{l}</p>
            ))}
            <p className="mt-1 text-xs text-zinc-500">{m.note}</p>
            <a
              href={m.href}
              {...(m.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="mt-5 inline-flex w-fit items-center gap-2 border border-zinc-900 px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-zinc-900 transition-colors hover:bg-zinc-900 hover:text-white"
            >
              {m.cta}
            </a>
          </div>
        ))}
      </div>

      {/* Hours + info */}
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <div className="flex gap-4 border border-zinc-200 bg-white p-6">
          <Clock size={20} className="shrink-0 text-kkaccent" />
          <div>
            <h3 className="text-sm font-medium">Customer Service Hours</h3>
            <p className="mt-1 text-sm text-zinc-500">Mon – Fri: 9:00 AM – 6:00 PM (ET)</p>
            <p className="text-sm text-zinc-500">Sat: 10:00 AM – 4:00 PM (ET)</p>
            <p className="text-sm text-zinc-500">Sun: Closed</p>
          </div>
        </div>
        <div className="flex gap-4 border border-zinc-200 bg-white p-6">
          <Truck size={20} className="shrink-0 text-kkaccent" />
          <div>
            <h3 className="text-sm font-medium">Shipping</h3>
            <p className="mt-1 text-sm text-zinc-500">
              We ship within the United States only. Orders outside the USA are not accepted.
            </p>
          </div>
        </div>
        <div className="flex gap-4 border border-zinc-200 bg-white p-6">
          <RotateCcw size={20} className="shrink-0 text-kkaccent" />
          <div>
            <h3 className="text-sm font-medium">Returns</h3>
            <p className="mt-1 text-sm text-zinc-500">
              7-day return window. Skincare and Bath &amp; Body products are non-returnable for hygiene reasons.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
